import _ from "lodash";
import React, { useState, useEffect, useCallback, memo } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import classNames from "classnames";
import { Detector } from "react-detect-offline";
import { useDispatch } from "Hooks/useDispatch";
import { Badge, Tooltip } from "@toddle-edu/ds-web";
import { ChevronRightOutlined } from "@toddle-edu/ds-icons";
import UIModal from "UIComponents/UIModal";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import NotificationCenter from "AppComponents/NotificationCenter";
import { graphql, compose } from "lib/apolloClient";
import { getNotificationsCountQuery } from "modules/CommonQuery";
import {
  getNotificationsCountFromCache,
  getUserCoursesIdFromCache,
} from "Teacher/modules/TeacherGraphqlHelpers";
import { getUserCoursesIdQuery } from "Teacher/modules/TeacherQuery";
import {
  hideIntercom,
  getCategoryTabsForNotification,
  addFeaturesWithKeyboardShortcuts,
  removeFeaturesWithKeyboardShortcuts,
  setActiveFeatureWithKeyboardShortcuts,
} from "modules/Services";
import {
  getUserInfo,
  isToddleLibraryRoute,
  setSelectedGlobalTrayIcon,
  setGlobalTrayRetracted,
  setGlobalTrayEnabledState,
  setGlobalTrayUnreadCount,
  getSampleOrgConfig,
  setUserCoursesIds,
} from "Login/modules/LoginModule";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import { getUserIdOnPortalType } from "Login/modules/LoginModule/LoginUtils";
import {
  CACHE_AND_NETWORK,
  NOTIFICATIONS,
  HELP_CENTER,
  MESSAGING,
} from "Constants/stringConstants";

import MessagingModal from "./MenuOptions/MessagingModal/MessagingModal";
import { getChatUnreadCountQuery } from "MessagingV2/components/ThreadList/Utils/ThreadListQueries";
import { getChatUnreadCountQueryFromCache } from "MessagingV2/components/ThreadList/Utils/ThreadListGraphqlHelpers";
import {
  isMessagingV2Enabled,
  getUnreadChatsCountQueryVariables,
} from "MessagingV2/modules/Utils";
import {
  getTrayOptions,
  getGlobalMenuTrayCountArr,
  checkIfShowNotificationInGlobalTrayEnabled,
} from "./Utils";
import classes from "./GlobalMenuTray.scss";
import { t } from "i18next";
import { getMemoizedFunction, onEnterOrSpaceKeyDown } from "Utils";
import { getIsAdminTab } from "Announcements/modules/utils";
// NOTE: this tray will be replaced with a newer version. Hence not considering making any important changes

const _GlobalMenuTray = props => {
  const {
    courses,
    notificationCount,
    userLoggedIn,
    isIntercomV2Enabled,
    isData: isNotificationData,
    showNotificationIconGlobal,
    showSprinklrChat,
    showMessagingIcon,
    onMessagingRoute,
    setSelectedGlobalTrayIcon: setSelectedIcon,
    messagingUserId,
    selectedIcon,
    sprinklrUnreadMessageCount,
    isSprinklrChatOpen,
    unreadChatCounts,
    hiddenGlobalTrayIcons,
    isGlobalTrayRetracted,
    setGlobalTrayRetracted,
    setGlobalTrayUnreadCount,
    showIntercom,
    setGlobalTrayEnabledState,
    refetchMessagingV2UnreadCount,
    addFeaturesWithKeyboardShortcuts,
    removeFeaturesWithKeyboardShortcuts,
    setActiveFeatureWithKeyboardShortcuts,
    userType,
    isNotificationShortcutActive,
    isMessagingShortcutActive,
  } = props;

  const [hoveredIcon, setHoveredIcon] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (courses) dispatch(setUserCoursesIds(courses));
  }, [courses]);

  const closeAllOptions = useCallback(() => {
    setSelectedIcon("");
  }, []);

  const trayOptions = getTrayOptions({
    userLoggedIn,
    isIntercomV2Enabled,
    isNotificationData,
    closeAllOptions,
    selectedIcon,
    showNotificationIconGlobal,
    showSprinklrChat,
    showMessagingIcon,
    sprinklrUnreadMessageCount,
    hiddenGlobalTrayIcons,
    showIntercom,
    hoveredIcon,
  });
  const alUnreadChatCount = _.get(unreadChatCounts, "allUnreadCount", 0);

  const _getTotalUnreadCount = (
    notificationCount,
    alUnreadChatCount,
    sprinklrUnreadMessageCount
  ) => {
    let totalUnreadCount = 0;
    const globalMenuTrayCountArr = getGlobalMenuTrayCountArr({
      notificationCount,
      totalUnreadChatCount: alUnreadChatCount,
      sprinklrUnreadMessageCount,
      trayOptions,
    });
    _.forEach(globalMenuTrayCountArr, globalMenuTrayCountItem => {
      if (
        !_.includes(
          hiddenGlobalTrayIcons,
          _.get(globalMenuTrayCountItem, "key")
        )
      ) {
        totalUnreadCount += _.get(globalMenuTrayCountItem, "value") ?? 0;
      }
    });
    return totalUnreadCount;
  };
  const getTotalUnreadCount = getMemoizedFunction(_getTotalUnreadCount);

  /**
   * Update redux state of activeFeatureWithKeyboardShortcuts, when user clicks on a messaging or notification icon and local state is updated
   */
  const setOrCleanActiveFeatureWithKeyboardShortcut = useCallback(key => {
    switch (key) {
      case "NOTIFICATIONS": {
        setActiveFeatureWithKeyboardShortcuts("GLOBAL_MENU_NOTIFICATION");
        break;
      }
      case "MESSAGING": {
        setActiveFeatureWithKeyboardShortcuts("GLOBAL_MENU_MESSAGING");
        break;
      }
      default: {
        setActiveFeatureWithKeyboardShortcuts("");
      }
    }
  }, []);

  const handleGlobalMenuTrayClick = useCallback(
    key => {
      try {
        const { onClick = _.noop } = _.find(trayOptions, { key });
        if (!_.isEqual(selectedIcon, key)) {
          setSelectedIcon(key);
          onClick();
          setOrCleanActiveFeatureWithKeyboardShortcut(key);
        } else {
          setSelectedIcon("");
          setOrCleanActiveFeatureWithKeyboardShortcut("");
        }
      } catch (e) {
        throw e;
      }
    },
    [trayOptions, selectedIcon]
  );

  useEffect(() => {
    const totalUnreadCount = getTotalUnreadCount(
      notificationCount,
      alUnreadChatCount,
      sprinklrUnreadMessageCount
    );
    setGlobalTrayUnreadCount(totalUnreadCount);
  }, [notificationCount, alUnreadChatCount, sprinklrUnreadMessageCount]);

  useEffect(() => {
    const featuresToBeAdded = [],
      featuresToBeRemoved = [];

    const isNotificationGlobalShortcutAvailable =
      showNotificationIconGlobal && userType === "staff";

    const isMessagingGlobalShortcutAvailable =
      showMessagingIcon && userType === "staff";

    if (isNotificationGlobalShortcutAvailable) {
      featuresToBeAdded.push("GLOBAL_MENU_NOTIFICATION");
    } else {
      featuresToBeRemoved.push("GLOBAL_MENU_NOTIFICATION");
    }

    if (isMessagingGlobalShortcutAvailable) {
      featuresToBeAdded.push("GLOBAL_MENU_MESSAGING");
    } else {
      featuresToBeRemoved.push("GLOBAL_MENU_MESSAGING");
    }

    if (!_.isEmpty(featuresToBeAdded)) {
      addFeaturesWithKeyboardShortcuts(featuresToBeAdded);
    }
    if (!_.isEmpty(featuresToBeRemoved)) {
      removeFeaturesWithKeyboardShortcuts(featuresToBeRemoved);
    }

    return () => {
      if (!_.isEmpty(featuresToBeAdded)) {
        removeFeaturesWithKeyboardShortcuts(featuresToBeAdded);
      }
    };
  }, [showNotificationIconGlobal, showMessagingIcon, userType]);

  /**
   * Update local state, when redux state of activeFeatureWithKeyboardShortcuts is updated from/to calendar
   */
  useEffect(() => {
    if (isNotificationShortcutActive && selectedIcon !== "NOTIFICATIONS") {
      handleGlobalMenuTrayClick("NOTIFICATIONS");
      if (isGlobalTrayRetracted) {
        setGlobalTrayRetracted(false);
      }
    }

    if (isMessagingShortcutActive && selectedIcon !== "MESSAGING") {
      handleGlobalMenuTrayClick("MESSAGING");
      if (isGlobalTrayRetracted) {
        setGlobalTrayRetracted(false);
      }
    }

    if (
      !isNotificationShortcutActive &&
      !isMessagingShortcutActive &&
      _.includes(["NOTIFICATIONS", "MESSAGING"], selectedIcon)
    ) {
      setSelectedIcon("");
    }
  }, [isNotificationShortcutActive, isMessagingShortcutActive]);

  const toggleShowNotificationCenter = () => {
    toggleTrayOption(NOTIFICATIONS);
  };

  const toggleShowMessagingModal = () => {
    toggleTrayOption(MESSAGING);
  };

  const toggleTrayOption = key => {
    if (_.isEmpty(selectedIcon)) {
      setSelectedIcon(key);
      setOrCleanActiveFeatureWithKeyboardShortcut(key);
    } else {
      setSelectedIcon("");
      setOrCleanActiveFeatureWithKeyboardShortcut("");
    }
  };

  const toggleTrayRetracted = () => {
    setGlobalTrayRetracted(!isGlobalTrayRetracted);
    if (!isGlobalTrayRetracted) {
      setSelectedIcon("");
    }
  };

  const renderIcon = ({ key, icon }) => {
    switch (key) {
      case NOTIFICATIONS: {
        return (
          <>
            {icon}
            <Badge
              variant={
                _.isEqual(selectedIcon, NOTIFICATIONS)
                  ? "subtle"
                  : "notifications"
              }
              size={"medium"}
              value={notificationCount}
              type={"numeric"}
            ></Badge>
          </>
        );
      }
      case MESSAGING: {
        return (
          <>
            {icon}
            <Badge
              variant={
                _.isEqual(selectedIcon, MESSAGING) ? "subtle" : "notifications"
              }
              size={"medium"}
              value={alUnreadChatCount}
              type={"numeric"}
            ></Badge>
          </>
        );
      }
      default: {
        return icon;
      }
    }
  };

  const renderGlobalMenuTrayOptionsUI = trayOptions => {
    return _.map(trayOptions, ({ key, icon, onClick = () => {} }) => {
      const onMouseEnter = () => {
        setHoveredIcon(key);
      };

      const onMouseLeave = () => {
        setHoveredIcon("");
      };

      const iconContainerClass = classNames({
        [classes.iconContainer]: true,
        [classes.iconContainerHelpCenter]: key === HELP_CENTER,
        [classes.selectedIcon]: selectedIcon === key,
        [classes.messagingIconContainer]: key === MESSAGING,
      });

      const countClass = classNames({
        [classes.count]: true,
        [classes.countSelected]: selectedIcon === key,
      });

      return (
        <div className={classes.iconWithBadge} key={key}>
          <div
            className={iconContainerClass}
            role="button"
            tabIndex={0}
            aria-label={_.startCase(_.toLower(key))}
            onClick={() => handleGlobalMenuTrayClick(key)}
            onKeyDown={e =>
              onEnterOrSpaceKeyDown(e, () => handleGlobalMenuTrayClick(key))
            }
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {renderIcon({ key, icon })}
          </div>
          <div className={classes.borderInBetween} />
        </div>
      );
    });
  };

  const renderGlobalMenuTrayOptions = () => {
    if (_.isEmpty(trayOptions)) {
      setGlobalTrayEnabledState(false);
    }
    return (
      <>
        {renderGlobalMenuTrayOptionsUI(trayOptions)}
        <Tooltip
          tooltip={t("common:hide_magic_tray")}
          placement="topRight"
          onClick={toggleTrayRetracted}
        >
          <div className={classes.iconWithBadge}>
            <div className={classes.iconContainer}>
              <ChevronRightOutlined size="xx-small" variant={"subtle"} />
            </div>
          </div>
        </Tooltip>
      </>
    );
  };

  const menuTrayOptions = renderGlobalMenuTrayOptions();
  const showNotificationCenter = selectedIcon === NOTIFICATIONS;
  const showMessagingList =
    showMessagingIcon &&
    (selectedIcon === MESSAGING || !_.isNull(messagingUserId));

  useEffect(() => {
    if (onMessagingRoute && selectedIcon === MESSAGING) {
      setSelectedIcon("");
    }
  }, [onMessagingRoute, selectedIcon]);

  // We are hiding and displaying the tray to prevent multiple Sprinklr aapHandsakes
  const trayButtonContainerClasses = classNames({
    [classes.trayButtonContainer]: !isSprinklrChatOpen,
    [classes.trayButtonContainerExpanded]:
      !isSprinklrChatOpen && !isGlobalTrayRetracted,
    [classes.hidetrayButtonContainer]: isSprinklrChatOpen,
  });

  const handleNetworkChange = isOnline => {
    if (isOnline && _.isFunction(refetchMessagingV2UnreadCount))
      refetchMessagingV2UnreadCount();
  };

  return (
    <>
      <Detector
        polling={false}
        render={() => null}
        onChange={handleNetworkChange}
      />
      {_.isEmpty(menuTrayOptions) ? null : (
        <div className={trayButtonContainerClasses}>
          {showMessagingList ? (
            <MessagingModal
              unreadChatCounts={unreadChatCounts}
              toggleShowMessagingModal={toggleShowMessagingModal}
            />
          ) : null}
          <div className={classes.container}>
            {renderGlobalMenuTrayOptions()}
          </div>
        </div>
      )}
      {showNotificationCenter ? (
        <UIModal
          isOpen={true}
          modalContent={classes.modalClass}
          overlayContainer={classes.overlayContainer}
          onRequestClose={toggleShowNotificationCenter}
          isGlobalMenuTray={true}
        >
          <NotificationCenter
            courses={courses}
            showNotificationCenter={true}
            toggleShowNotificationCenter={toggleShowNotificationCenter}
          />
        </UIModal>
      ) : null}
    </>
  );
};
const GlobalMenuTray = memo(_GlobalMenuTray);

const mapActionCreators = {
  hideIntercom,
  setSelectedGlobalTrayIcon,
  setGlobalTrayRetracted,
  setGlobalTrayUnreadCount,
  setGlobalTrayEnabledState,
  addFeaturesWithKeyboardShortcuts,
  removeFeaturesWithKeyboardShortcuts,
  setActiveFeatureWithKeyboardShortcuts,
};

const _checkIsMypUbdLibraryRoute = routeCourseId => {
  const toddleLibraryOrgs = getSampleOrgConfig();
  return routeCourseId
    ? _.some(toddleLibraryOrgs, ({ demoCourse }) => demoCourse == routeCourseId)
    : false;
};
const checkIsMypUbdLibraryRoute = getMemoizedFunction(
  _checkIsMypUbdLibraryRoute
);

const _getUnreadChatsCountQueryVariablesMemoize = () => ({
  ...getUnreadChatsCountQueryVariables(),
  portalType: "PLANNER",
});
const getUnreadChatsCountQueryVariablesMemoize = getMemoizedFunction(
  _getUnreadChatsCountQueryVariablesMemoize
);

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const userType = _.get(userInfo, "user_type");

  const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });

  const categoryTabs = getCategoryTabsForNotification({
    userType,
    isCurriculumProgramFree,
  });

  const userId = getUserIdOnPortalType({ state, portalType: "PLANNER" });

  const userEntityType = _.get(userInfo, "userEntityType");

  const onMessagingRoute = _.includes(
    _.get(ownProps, "location.pathname"),
    "/messaging"
  );

  const routeCourseId = _.get(ownProps, "params.course_id");

  const isMypUbdLibraryRoute = checkIsMypUbdLibraryRoute(routeCourseId);

  const showMessagingIcon =
    !onMessagingRoute &&
    !isToddleLibraryRoute() &&
    !isMypUbdLibraryRoute &&
    isMessagingV2Enabled();

  const unreadChatCountQueryVariables = getUnreadChatsCountQueryVariablesMemoize();

  const selectedIcon = _.get(state, "login.selectedIcon");
  const messagingUserId = _.get(state, "login.messagingUser.id");

  const isGlobalTrayRetracted = _.get(state, "login.isGlobalTrayRetracted");
  const hiddenGlobalTrayIcons = _.get(state, "login.hiddenGlobalTrayIcons");
  const showIntercom = _.get(state, "app_services.showIntercom", true);
  const showNotificationIconGlobal =
    _.get(state, "app_services.showNotificationIconGlobal", true) &&
    checkIfShowNotificationInGlobalTrayEnabled();

  const isAdminTab = getIsAdminTab(state);

  return {
    isData: true,
    isLoading: false,
    userId,
    userEntityType,
    categoryTabs,
    selectedIcon,
    activeTab: _.get(state, "login.activeTab"),
    showNotificationIconGlobal,
    showMessagingIcon,
    onMessagingRoute,
    unreadChatCountQueryVariables,
    skipNotificationCountQuery:
      !userId || !userEntityType || _.isEmpty(categoryTabs),
    messagingUserId,
    sprinklrUnreadMessageCount: _.get(
      state,
      "login.sprinklrUnreadMessageCount",
      0
    ),
    isSprinklrChatOpen: _.get(state, "login.isSprinklrChatOpen", false),
    isGlobalTrayRetracted,
    hiddenGlobalTrayIcons,
    showIntercom,
    skipGetUserCoursesIdQuery: !checkIfShowNotificationInGlobalTrayEnabled(),
    isAdminTab,
    isNotificationShortcutActive:
      _.get(state, "app_services.activeFeatureWithKeyboardShortcuts") ===
      "GLOBAL_MENU_NOTIFICATION",
    isMessagingShortcutActive:
      _.get(state, "app_services.activeFeatureWithKeyboardShortcuts") ===
      "GLOBAL_MENU_MESSAGING",
    userType,
  };
};

const pusherEvents = WrappedComponent => {
  const PusherComponent = props => {
    const { getNotificationsCount, getChatUnreadCount } = props;

    const onPusherEventTriggered = useCallback(
      data => {
        if (
          _.get(data, "event") === "notificationAdded" &&
          getNotificationsCount
        ) {
          getNotificationsCount.refetch();
        } else if (_.get(data, "event") === "chatMessageReceived") {
          if (getChatUnreadCount) {
            getChatUnreadCount.refetch();
          }
        }
      },
      [getNotificationsCount, getChatUnreadCount]
    );

    return (
      <WrappedComponent
        {...props}
        onPusherEventTriggered={onPusherEventTriggered}
      />
    );
  };

  return PusherComponent;
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapActionCreators),
  graphql(getNotificationsCountQuery, {
    name: "getNotificationsCount",
    options: ({ userId, userEntityType, categoryTabs }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: {
        id: userId,
        type: userEntityType,
        notificationCategoryFilter: categoryTabs,
        portalType: "PLANNER",
      },
      context: {
        useHttpLink: true,
      },
    }),
    skip: ({
      skipNotificationCountQuery,
      showNotificationIconGlobal,
      isAdminTab,
    }) =>
      skipNotificationCountQuery || !showNotificationIconGlobal || !isAdminTab,
    props({ getNotificationsCount, ownProps: { isData, isLoading } }) {
      const notification = getNotificationsCountFromCache(
        getNotificationsCount.variables
      );
      return {
        getNotificationsCount,
        isData: !_.isEmpty(notification) && isData,
        notificationCount: _.get(notification, "newCount", 0),
        isLoading:
          _.includes([1, 2], getNotificationsCount["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  graphql(getUserCoursesIdQuery, {
    name: "getUserCoursesId",
    options: ({ userId, userEntityType, courseFilters }) => ({
      fetchPolicy: CACHE_AND_NETWORK,
      variables: {
        id: userId,
        type: userEntityType,
        filters: courseFilters,
        portalType: "PLANNER",
      },
      context: {
        useHttpLink: true,
      },
    }),
    skip: ({ userId, userEntityType, skipGetUserCoursesIdQuery }) =>
      !userId || !userEntityType || skipGetUserCoursesIdQuery,

    props({ getUserCoursesId, ownProps: { isData, isLoading } }) {
      const queryData = getUserCoursesIdFromCache(getUserCoursesId.variables);
      return {
        isData: !_.isEmpty(queryData) && isData,
        courses: _.get(queryData, "courses", []),
        isLoading:
          _.includes([1, 2], getUserCoursesId["networkStatus"]) || isLoading,
      };
    },
  }),
  graphql(getChatUnreadCountQuery, {
    name: "getChatUnreadCount",
    options: ({ unreadChatCountQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: unreadChatCountQueryVariables,
      context: { useHttpLink: true },
    }),
    skip: ({ showMessagingIcon }) => !showMessagingIcon,
    props({ getChatUnreadCount, ownProps: { unreadChatCountQueryVariables } }) {
      const chatResult = getChatUnreadCountQueryFromCache(
        unreadChatCountQueryVariables
      );
      const unreadChatCounts = _.get(chatResult, "node.chats", {});

      return {
        getChatUnreadCount,
        isData: !_.isEmpty(chatResult),
        isLoading: _.includes([1, 2], getChatUnreadCount["networkStatus"]),
        unreadChatCounts,
        refetchMessagingV2UnreadCount: getChatUnreadCount.refetch,
      };
    },
  }),
  pusherEvents,
  withPusherBind(["notificationAdded", "chatMessageReceived"])
)(GlobalMenuTray);
